import { ChevronDown, LogOut, Plus, X, Menu } from 'lucide-react'
import { useState, useRef, useEffect } from 'react'
import type React from 'react'
import { useNavigate } from 'react-router-dom'
import Logo from '../assets/AuthLogo.svg'
import WaveLeft from '../assets/g161.svg'
import WaveRight from '../assets/g162.svg'
import { useUserProfile } from '../context/UserProfileContext'
import { AuthService } from '../utils/authService'
import { clearHmrcTokens, isHmrcAuthorized } from '../utils/hmrcTokenService'
import FilingModal from './FilingModal'
import HmrcAuthModal from './HmrcAuthModal'
import { prepareHmrcAuth, getFilingRoute } from '../utils/hmrcAuthHelper'

interface AuthenticatedLayoutProps {
  children: React.ReactNode
  activeTab?: 'dashboard' | 'filings' | 'companies' | 'settings'
  showWelcome?: boolean
}

const navItems = [
  { id: 'dashboard', label: 'Dashboard', path: '/dashboard' },
  { id: 'filings', label: 'Filings', path: '/history' },
  { id: 'companies', label: 'Companies', path: '/companies' },
  { id: 'settings', label: 'Settings', path: '/settings' },
] as const

const DARK_GRADIENT =
  'linear-gradient(174.48deg, rgb(18, 12, 13) 17.74%, rgba(18, 12, 13, 0.2) 126.6%), linear-gradient(to right, #0C2D54, #5F2222)'

const DOT_PATTERN_STYLE = {
  backgroundImage: `radial-gradient(circle, rgba(255,255,255,0.1) 1px, transparent 1px)`,
  backgroundSize: '20px 20px',
}

export default function AuthenticatedLayout({
  children,
  activeTab = 'dashboard',
  showWelcome = false,
}: AuthenticatedLayoutProps) {
  const { getUserDisplayName, isLoadingUser, user } = useUserProfile()
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isHmrcAuthModalOpen, setIsHmrcAuthModalOpen] = useState(false)
  const [hmrcAuthUrl, setHmrcAuthUrl] = useState<string | null>(null)
  const [isRequestingHmrcAuth, setIsRequestingHmrcAuth] = useState(false)
  const [selectedFilingType, setSelectedFilingType] = useState<string>('')
  const [isScrolled, setIsScrolled] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const isLoggingOutRef = useRef(false)
  const navigate = useNavigate()

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    setIsScrolled(e.currentTarget.scrollTop > 8)
  }

  useEffect(() => {
    document.documentElement.classList.add('authenticated-layout')
    document.body.classList.add('authenticated-layout')
    const root = document.getElementById('root')
    if (root) {
      root.classList.add('authenticated-layout')
    }

    return () => {
      document.documentElement.classList.remove('authenticated-layout')
      document.body.classList.remove('authenticated-layout')
      if (root) {
        root.classList.remove('authenticated-layout')
      }
    }
  }, [])

  useEffect(() => {
    if (!isDropdownOpen) return

    const handleClickOutside = (event: MouseEvent) => {
      if (isLoggingOutRef.current) return
      const target = event.target as Node
      if (dropdownRef.current && !dropdownRef.current.contains(target)) {
        setIsDropdownOpen(false)
      }
    }

    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside, true)
    }, 0)

    return () => {
      clearTimeout(timeoutId)
      document.removeEventListener('mousedown', handleClickOutside, true)
    }
  }, [isDropdownOpen])

  const handleLogout = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    isLoggingOutRef.current = true
    setIsDropdownOpen(false)
    clearHmrcTokens()
    AuthService.logout()
  }

  const handleFilingOptionSelect = async (option: string) => {
    setIsModalOpen(false)
    setSelectedFilingType(option)

    if (option === 'add-company') {
      navigate('/add-company')
      return
    }
    if (option === 'confirmation-statement-filing') {
      navigate('/companies-house-filing')
      return
    }
    if (option === 'self-assessment-sa' || option === 'ct600-filing') {
      const route = getFilingRoute(option)
      if (route) navigate(route)
      return
    }
    if (
      option === 'self-assessment' ||
      option === 'self-assessment-trad'
    ) {
      const authorized = await isHmrcAuthorized()
      if (authorized) {
        const route = getFilingRoute(option)
        if (route) navigate(route)
        return
      }
      setIsRequestingHmrcAuth(true)
      setIsHmrcAuthModalOpen(true)
      try {
        const result = await prepareHmrcAuth(option)
        if (result?.authUrl) setHmrcAuthUrl(result.authUrl)
      } catch (error) {
        console.error('Failed to prepare HMRC auth:', error)
        setIsHmrcAuthModalOpen(false)
      } finally {
        setIsRequestingHmrcAuth(false)
      }
    }
  }

  const getUserInitials = () => {
    const name = `${user?.firstName || ''} ${user?.lastName || ''}`.trim()
    if (name) {
      const parts = name.split(' ').filter(Boolean)
      return parts
        .slice(0, 2)
        .map(part => part.charAt(0).toUpperCase())
        .join('')
    }
    if (user?.email) return user.email.charAt(0).toUpperCase()
    return '?'
  }

  const handleRequestHmrcAuth = async () => {
    if (!selectedFilingType || isRequestingHmrcAuth) return
    setIsRequestingHmrcAuth(true)
    try {
      const result = await prepareHmrcAuth(selectedFilingType)
      if (result?.authUrl) setHmrcAuthUrl(result.authUrl)
    } catch (error) {
      console.error('Error preparing HMRC auth:', error)
    } finally {
      setIsRequestingHmrcAuth(false)
    }
  }

  return (
    <div className='fixed inset-0 overflow-hidden bg-[#F4F4F7]'>
      {/* Nav Bar — transparent over the hero, gains a dark background on scroll */}
      <div
        className='absolute top-0 left-0 right-0 z-20 transition-colors duration-200'
        style={{
          background:
            isScrolled || isMobileMenuOpen ? DARK_GRADIENT : 'transparent',
        }}
      >
        {(isScrolled || isMobileMenuOpen) && (
          <div
            className='absolute inset-0 opacity-20 overflow-hidden'
            style={DOT_PATTERN_STYLE}
          ></div>
        )}

        <div className='relative z-10 mx-auto w-full max-w-[1600px] px-4 sm:px-6 md:px-10 lg:px-20 pt-6 pb-4'>
          <div className='flex items-center justify-between'>
            {/* Left: Logo + Navigation */}
            <div className='flex items-center gap-10'>
              <img src={Logo} alt='BritFiler' className='h-8 w-auto' />
              <nav className='hidden lg:flex items-center gap-1'>
                {navItems.map(item => (
                  <button
                    key={item.id}
                    onClick={() => navigate(item.path)}
                    className={`px-5 py-2.5 rounded-[27px] text-base font-medium transition-colors cursor-pointer ${
                      activeTab === item.id
                        ? 'text-white'
                        : 'text-[#A4A7AE] hover:text-white'
                    }`}
                    style={{
                      backgroundColor:
                        activeTab === item.id
                          ? 'rgba(255, 255, 255, 0.12)'
                          : 'transparent',
                    }}
                  >
                    {item.label}
                  </button>
                ))}
              </nav>
            </div>

            {/* Right: Actions */}
            <div className='flex items-center gap-4'>
              <button
                onClick={() => setIsModalOpen(true)}
                className='bg-[#F04438] hover:bg-[#DC2626] text-white rounded-[27px] px-5 py-2.5 flex items-center gap-2 text-base font-medium cursor-pointer transition-colors'
              >
                <Plus className='w-5 h-5' />
                <span className='hidden sm:inline'>New Filing</span>
              </button>

             

              <div
                className='relative hidden lg:flex items-center gap-1'
                ref={dropdownRef}
              >
                <button
                  type='button'
                  onClick={e => {
                    e.stopPropagation()
                    setIsDropdownOpen(!isDropdownOpen)
                  }}
                  className='w-12 h-12 rounded-full flex items-center justify-center cursor-pointer transition-colors overflow-hidden'
                  style={{ backgroundColor: 'rgba(255, 255, 255, 0.1)' }}
                >
                  <div className='w-10 h-10 rounded-full bg-white/20 text-white flex items-center justify-center font-semibold text-sm uppercase'>
                    {isLoadingUser ? '...' : getUserInitials()}
                  </div>
                </button>
                <ChevronDown
                  className={`w-5 h-5 text-white transition-transform cursor-pointer ${
                    isDropdownOpen ? 'rotate-180' : ''
                  }`}
                  onClick={e => {
                    e.stopPropagation()
                    setIsDropdownOpen(!isDropdownOpen)
                  }}
                />

                {isDropdownOpen && (
                  <div
                    className='absolute right-0 top-full mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-[100]'
                    onMouseDown={e => e.stopPropagation()}
                    tabIndex={-1}
                    role='menu'
                  >
                    <div className='px-4 py-2 border-b border-gray-100'>
                      <p className='text-sm font-medium text-gray-900'>
                        {getUserDisplayName()}
                      </p>
                      <p className='text-xs text-gray-500'>Manage your tax</p>
                    </div>
                    <button
                      onClick={handleLogout}
                      className='w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2 cursor-pointer'
                    >
                      <LogOut className='w-4 h-4' />
                      Logout
                    </button>
                  </div>
                )}
              </div>

              <button
                type='button'
                onClick={() => setIsMobileMenuOpen(prev => !prev)}
                className='lg:hidden text-white rounded-full border border-white/30 p-2 hover:bg-white/10 focus:outline-none'
                aria-label='Toggle navigation'
              >
                {isMobileMenuOpen ? (
                  <X className='w-5 h-5' />
                ) : (
                  <Menu className='w-5 h-5' />
                )}
              </button>
            </div>
          </div>

          {/* Mobile menu */}
          {isMobileMenuOpen && (
            <div className='mt-4 rounded-2xl border border-white/10 bg-white/10 backdrop-blur-md p-4 lg:hidden'>
              <div className='flex items-center gap-3 mb-3'>
                <div className='w-10 h-10 rounded-full bg-white/20 text-white flex items-center justify-center font-semibold text-sm uppercase'>
                  {isLoadingUser ? '...' : getUserInitials()}
                </div>
                <div>
                  <p className='text-sm font-medium text-white'>
                    {isLoadingUser ? '...' : getUserDisplayName()}
                  </p>
                  <p className='text-xs text-gray-200'>Manage your tax</p>
                </div>
              </div>
              <nav className='flex flex-col gap-1'>
                {navItems.map(item => (
                  <button
                    key={item.id}
                    onClick={() => {
                      navigate(item.path)
                      setIsMobileMenuOpen(false)
                    }}
                    className={`px-4 py-2 rounded-full text-left cursor-pointer ${
                      activeTab === item.id
                        ? 'bg-white/12 text-white'
                        : 'text-gray-300 hover:text-white hover:bg-white/8'
                    }`}
                  >
                    {item.label}
                  </button>
                ))}
                <button
                  onClick={handleLogout}
                  className='px-4 py-2 rounded-full text-left text-gray-300 hover:text-white hover:bg-white/8 flex items-center gap-2 cursor-pointer'
                >
                  <LogOut className='w-4 h-4' />
                  Logout
                </button>
              </nav>
            </div>
          )}
        </div>
      </div>

      {/* Scrollable Content Area */}
      <div
        onScroll={handleScroll}
        className='absolute inset-0 overflow-y-auto bg-[#F4F4F7] scrollbar-hide'
      >
        {/* Dark gradient zone — always present, same height on every page.
            Sits behind the transparent navbar so they read as one surface. */}
        <div
          className='relative overflow-hidden'
          style={{ background: DARK_GRADIENT }}
        >
          <div
            className='absolute inset-0 opacity-20'
            style={DOT_PATTERN_STYLE}
          ></div>

          {/* Decorative dotted-wave flourishes */}
          <img
            src={WaveLeft}
            alt=''
            aria-hidden='true'
            className='pointer-events-none select-none absolute top-0 right-0 w-[45%] max-w-[640px]'
          />
          <img
            src={WaveRight}
            alt=''
            aria-hidden='true'
            className='pointer-events-none select-none absolute top-0 left-0 h-full w-auto max-w-[40%] object-cover object-left'
          />

          {showWelcome ? (
            <div className='relative z-10 mx-auto w-full max-w-[1600px] px-4 sm:px-6 md:px-10 lg:px-20 pt-[116px] pb-40'>
              <h1 className='text-3xl sm:text-[40px] leading-[1.5] tracking-[-1.2px] text-left'>
                <span className='font-extralight text-[#A4A7AE]'>
                  Welcome,{' '}
                </span>
                <span className='font-bold text-white'>
                  {isLoadingUser ? '...' : getUserDisplayName()}
                </span>
              </h1>
              <p className='text-base font-medium text-[#A4A7AE] tracking-[-0.32px] text-left'>
                We are here to help manage your tax!
              </p>
            </div>
          ) : (
            /* Same visual height as the welcome variant (+navbar clearance) */
            <div className='relative z-10 h-[284px]' />
          )}
        </div>

        {/* Content overlaps into the dark zone */}
        <div className='-mt-28 relative z-10 mx-auto w-full max-w-[1600px] px-4 sm:px-6 md:px-10 lg:px-20 pb-8'>
          {children}
        </div>
      </div>

      {/* Global Modals */}
      <FilingModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSelectOption={handleFilingOptionSelect}
        userEmail={user?.email}
      />
      <HmrcAuthModal
        isOpen={isHmrcAuthModalOpen}
        onClose={() => setIsHmrcAuthModalOpen(false)}
        authUrl={hmrcAuthUrl}
        isRequestingAuth={isRequestingHmrcAuth}
        onAuthorize={handleRequestHmrcAuth}
        filingType={selectedFilingType}
      />
    </div>
  )
}
